/**
 * Tenant detail page (Xamun Owner admin). Renders everything the
 * /api/admin/tenants/:id endpoint returns — owner, members, counts,
 * feature flags, recent admin actions — plus the lifecycle action
 * buttons (suspend/resume/archive) and an inline flag editor.
 */

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { ApiError} from "../../api/client";
import { apiDelete, apiGet, apiPost, apiPut } from "../../api/client";
import { AdminShell } from "../../components/admin/AdminShell";

interface FeatureFlag {
  flagKey: string;
  value: unknown;
  setBy: string;
  setAt: string;
  note: string | null;
}

interface AdminAction {
  id: string;
  actorEmail: string;
  action: string;
  payload: Record<string, unknown>;
  createdAt: string;
}

interface TenantDetail {
  tenant: {
    id: string;
    name: string;
    companyId: string;
    state: "active" | "paused" | "archived";
    cxoRoleTag: string;
    defaultDomain: string | null;
    createdAt: string;
    updatedAt: string;
  };
  owner: { id: string; email: string; displayName: string } | null;
  members: Array<{
    userId: string;
    email: string | null;
    displayName: string | null;
    role: string;
    accepted: boolean;
  }>;
  counts: { plays: number; cards: number };
  featureFlags: FeatureFlag[];
  recentAdminActions: AdminAction[];
}

export function AdminTenantDetail() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const detailQ = useQuery<TenantDetail, ApiError>({
    queryKey: ["admin-tenant", tenantId],
    queryFn: () => apiGet(`/api/admin/tenants/${tenantId}`),
    enabled: !!tenantId,
    retry: false,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin-tenant", tenantId] });

  const lifecycleM = useMutation<unknown, ApiError, "suspend" | "resume" | "archive">({
    mutationFn: (action) =>
      apiPost(`/api/admin/tenants/${tenantId}/${action}`, {}),
    onSuccess: invalidate,
  });

  if (detailQ.isLoading) {
    return (
      <AdminShell>
        <main className="review-page" style={{ margin: "0 auto" }}>Loading…</main>
      </AdminShell>
    );
  }
  if (detailQ.error) {
    return (
      <AdminShell>
        <main className="review-page" style={{ margin: "0 auto" }}>
          <div className="review-banner error">
            {detailQ.error.status === 403
              ? "You're not on the Xamun admin allow-list."
              : detailQ.error.status === 404
              ? "Tenant not found."
              : `Could not load tenant (${detailQ.error.status}).`}
          </div>
        </main>
      </AdminShell>
    );
  }
  if (!detailQ.data) return null;

  const d = detailQ.data;
  const isArchived = d.tenant.state === "archived";

  return (
    <AdminShell>
    <main className="review-page" style={{ margin: "0 auto" }}>
      <button
        type="button"
        className="review-back"
        onClick={() => navigate("/admin/tenants")}
      >
        ← Tenants
      </button>

      <p className="review-eyebrow">{d.tenant.companyId}</p>
      <h1 className="review-title">{d.tenant.name}</h1>
      <p className="review-meta">
        <strong>State:</strong> {d.tenant.state} · <strong>Supervisor role:</strong>{" "}
        {d.tenant.cxoRoleTag} · <strong>Created:</strong>{" "}
        {new Date(d.tenant.createdAt).toLocaleDateString()}
      </p>

      <div className="review-actions" style={{ marginTop: 16 }}>
        {d.tenant.state === "active" && (
          <button
            type="button"
            className="review-btn return"
            disabled={lifecycleM.isPending}
            onClick={() => lifecycleM.mutate("suspend")}
          >
            Suspend
          </button>
        )}
        {d.tenant.state === "paused" && (
          <button
            type="button"
            className="review-btn approve"
            disabled={lifecycleM.isPending}
            onClick={() => lifecycleM.mutate("resume")}
          >
            Resume
          </button>
        )}
        {!isArchived && (
          <button
            type="button"
            className="review-btn return"
            disabled={lifecycleM.isPending}
            onClick={() => {
              if (confirm("Archive this tenant? This is one-way.")) {
                lifecycleM.mutate("archive");
              }
            }}
          >
            Archive
          </button>
        )}
      </div>

      {lifecycleM.error && (
        <div className="review-banner error" style={{ marginTop: 12 }}>
          {lifecycleM.error.status === 409
            ? "Archived tenants cannot be reactivated."
            : `Could not change state (${lifecycleM.error.status}).`}
        </div>
      )}

      <section className="review-section">
        <h2 className="review-section-title">Activity</h2>
        <div style={{ display: "flex", gap: 16, fontSize: 14, color: "var(--text-2, #475569)" }}>
          <div><strong>{d.counts.plays}</strong> routines</div>
          <div><strong>{d.counts.cards}</strong> cards</div>
          <div><strong>{d.members.length}</strong> members</div>
        </div>
      </section>

      <section className="review-section">
        <h2 className="review-section-title">Owner</h2>
        {d.owner ? (
          <div className="review-meta">
            <strong>{d.owner.displayName}</strong> · {d.owner.email}
          </div>
        ) : (
          <div style={{ color: "var(--text-3)" }}>No owner record.</div>
        )}
      </section>

      <section className="review-section">
        <h2 className="review-section-title">Members ({d.members.length})</h2>
        <ul className="review-comment-list">
          {d.members.map((m) => (
            <li key={m.userId} className="review-comment">
              <div className="review-comment-head">
                <strong className="author">{m.displayName ?? m.email ?? m.userId}</strong>
                <span className="review-comment-decision approve">{m.role}</span>
                {!m.accepted && (
                  <span className="review-comment-decision note">pending</span>
                )}
                <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-3)" }}>
                  {m.email ?? ""}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <FeatureFlagsSection
        tenantId={tenantId!}
        flags={d.featureFlags}
        disabled={isArchived}
        onChanged={invalidate}
      />

      <section className="review-section">
        <h2 className="review-section-title">Recent admin actions</h2>
        {d.recentAdminActions.length === 0 ? (
          <div style={{ color: "var(--text-3)" }}>No admin actions yet.</div>
        ) : (
          <ul className="review-comment-list">
            {d.recentAdminActions.map((a) => (
              <li key={a.id} className="review-comment">
                <div className="review-comment-head">
                  <strong className="author">{a.action}</strong>
                  <span style={{ color: "var(--text-3)" }}>by {a.actorEmail}</span>
                  <span style={{ marginLeft: "auto", fontSize: 11 }}>
                    {new Date(a.createdAt).toLocaleString()}
                  </span>
                </div>
                {Object.keys(a.payload).length > 0 && (
                  <pre style={{
                    margin: "6px 0 0", fontSize: 11.5, padding: 8,
                    background: "var(--bg-soft, #f1f5f9)", borderRadius: 4,
                    overflowX: "auto",
                  }}>
                    {JSON.stringify(a.payload, null, 2)}
                  </pre>
                )}
              </li>
            ))}
          </ul>
        )}
        <p style={{ marginTop: 8, fontSize: 11, color: "var(--text-3)" }}>
          Full audit log: <Link to="/admin/audit">view all</Link>
        </p>
      </section>
    </main>
    </AdminShell>
  );
}

function FeatureFlagsSection({
  tenantId,
  flags,
  disabled,
  onChanged,
}: {
  tenantId: string;
  flags: FeatureFlag[];
  disabled: boolean;
  onChanged: () => void;
}) {
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("true");
  const [newNote, setNewNote] = useState("");

  const setM = useMutation<unknown, ApiError, { key: string; value: unknown; note?: string }>({
    mutationFn: ({ key, value, note }) =>
      apiPut(`/api/admin/tenants/${tenantId}/flags/${encodeURIComponent(key)}`, {
        value,
        ...(note && { note }),
      }),
    onSuccess: () => {
      setNewKey("");
      setNewValue("true");
      setNewNote("");
      onChanged();
    },
  });

  const delM = useMutation<unknown, ApiError, string>({
    mutationFn: (key) =>
      apiDelete(`/api/admin/tenants/${tenantId}/flags/${encodeURIComponent(key)}`),
    onSuccess: onChanged,
  });

  function parseValue(raw: string): unknown {
    if (raw === "true") return true;
    if (raw === "false") return false;
    if (raw === "null") return null;
    const n = Number(raw);
    if (raw !== "" && Number.isFinite(n)) return n;
    try { return JSON.parse(raw); } catch { return raw; }
  }

  return (
    <section className="review-section">
      <h2 className="review-section-title">Feature flags ({flags.length})</h2>

      {flags.length === 0 ? (
        <div style={{ color: "var(--text-3)" }}>No flags set.</div>
      ) : (
        <ul className="review-comment-list">
          {flags.map((f) => (
            <li key={f.flagKey} className="review-comment">
              <div className="review-comment-head">
                <code className="author" style={{ fontFamily: "monospace" }}>
                  {f.flagKey}
                </code>
                <span className="review-comment-decision approve">
                  {JSON.stringify(f.value)}
                </span>
                <span style={{ color: "var(--text-3)", fontSize: 11 }}>
                  by {f.setBy} · {new Date(f.setAt).toLocaleDateString()}
                </span>
                <button
                  type="button"
                  className="review-btn note"
                  style={{ marginLeft: "auto", minWidth: 80 }}
                  disabled={disabled || delM.isPending}
                  onClick={() => {
                    if (confirm(`Delete flag ${f.flagKey}?`)) delM.mutate(f.flagKey);
                  }}
                >
                  Delete
                </button>
              </div>
              {f.note && (
                <p className="review-comment-body" style={{ marginTop: 4 }}>{f.note}</p>
              )}
            </li>
          ))}
        </ul>
      )}

      {!disabled && (
        <div className="review-form" style={{ marginTop: 12 }}>
          <h3 className="review-section-title" style={{ marginBottom: 10 }}>
            Set a flag
          </h3>
          <div className="spotted-form-row">
            <label className="spotted-field">
              <span>Flag key</span>
              <input
                className="review-input"
                placeholder="preview.spotted_phase_d"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
              />
            </label>
            <label className="spotted-field">
              <span>Value</span>
              <input
                className="review-input"
                placeholder="true / false / number / JSON"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
              />
            </label>
          </div>
          <label className="review-field">
            <span className="review-field-label">
              <strong>Note (optional)</strong>
            </span>
            <input
              className="review-input"
              placeholder="Early-adopter pilot, expires 2026-Q4"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
            />
          </label>
          {setM.error && (
            <div className="review-banner error" style={{ marginTop: 12 }}>
              Could not set flag ({setM.error.status}).
            </div>
          )}
          <div className="review-actions" style={{ marginTop: 14 }}>
            <button
              type="button"
              className="review-btn approve"
              disabled={!newKey.trim() || setM.isPending}
              onClick={() => setM.mutate({
                key: newKey.trim(),
                value: parseValue(newValue),
                ...(newNote.trim() && { note: newNote.trim() }),
              })}
            >
              {setM.isPending ? "Setting…" : "Set flag"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
